import { Component, Input, OnInit } from '@angular/core';
import { BasicSunburstChartConfig } from '@app/data/interface/widget-config';
import { forkJoin } from 'rxjs';
import { take, finalize } from 'rxjs/operators';
import moment from 'moment-timezone';
import { EChartsOption, SeriesOption } from 'echarts';
import BasicSunburstChartTemplate from '@app/data/widget-config-templates/basic-sunburst-chart';
import { Analyte } from '@app/data/services/types/EnvironmentalData';
import { EnvironmentalDataSource } from '@app/data/services/types/EnvironmentalDataSource';
import { BaseFilteredEnvironmentalDataChartComponent } from '../base-filtered-environmental-data-chart-component';

interface SunburstResponse {
  granularity: 'day' | 'month' | 'year';
  data: SunburstRow[];
  environmentalDataSources: EnvironmentalDataSource[];
  analytes: Analyte[];
  entities: SunburstEntity[];
}

/**
 * Metadata about species extracted from JSON blob in raw environmental data rows.
 */
interface SpeciesCollectionMetadata {
  SVL: string;
  Sex: string;
  Gravid: string;
  Vegetation: string;
  'Tail Status': string;
  'Height off ground cm': string;
}

interface SunburstEntity {
  id: number;
  name: string;
  unique_id: string;
  tags: string[];
  organisation_id: number | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  icon: string;
}

interface SunburstRow {
  environmental_data_source_id: string;
  analyte_id: string;
  observation_entity_id: number;
  date_time: string;
  avg: number;
  min: number;
  max: number;
  sum: number;
  stddev: number | null;
  stddev_pop: number;
  stddev_samp: number | null;
  variance: number | null;
  var_pop: number;
  var_samp: number | null;
  percentile_25: number;
  percentile_50: number;
  percentile_75: number;
  percentile_90: number;
  percentile_95: number;
  percentile_99: number;
  mode: number;
  collection_metadata: SpeciesCollectionMetadata[];
  count: number;
  entities: SunburstEntity[];
}

/**
 * Computes the percentage label value for a sunburst chart segment.
 * @param params The parameters containing tree path information and percent value.
 * @param dataItem The data item for the current segment.
 * @returns A function that returns the formatted percentage string or null.
 */
function computePercentLabelValue(params: any, dataItem: any) {
  const pctParam = params?.percent;
  if (pctParam != null) {
    const pct = Number(pctParam);
    return Number.isFinite(pct) ? `${pct.toFixed(1)}%` : null;
  }

  const path = params?.treePathInfo;
  if (!Array.isArray(path) || path.length < 2) return null;

  const parent = path[path.length - 2];
  const parentValue = Number(parent?.value);
  const childValue = Number(dataItem.value);
  if (!Number.isFinite(parentValue) || parentValue === 0 || !Number.isFinite(childValue)) return null;

  const pct = (childValue / parentValue) * 100;
  return Number.isFinite(pct) ? `${pct.toFixed(1)}%` : null;
}

@Component({
  selector: 'app-basic-sunburst-chart',
  templateUrl: './basic-sunburst-chart.component.html',
  styleUrls: ['./basic-sunburst-chart.component.scss'],
})
export class BasicSunburstChartComponent extends BaseFilteredEnvironmentalDataChartComponent {
  @Input() config!: BasicSunburstChartConfig;
  chartOptions: EChartsOption | null = null;
  id: string = 'basic-sunburst-chart-x';
  environmentalDataSources: EnvironmentalDataSource[] = [];
  dataSourceLabelMap: Record<string, string> = {};

  ngOnInit() {
    super.ngOnInit();
    this.id = `basic-sunburst-chart-${this.config.dataConfig.widgetId}`;
    this.chartTitle = this.config.dataConfig.title || '';
    this.chartSubtitle = this.config.dataConfig.subtitle || '';
    this.domain = this.config.dataConfig.domain || '';

    this.getDataSourceLabels().then(() => {
      // Ensure data source labels are loaded before setting data
      this.setData();
    });
  }

  /**
   * Clear any displayed chart data so the UI does not show stale results while
   * new data is being fetched. Implements the abstract contract from the base class.
   */
  protected clearChartData(): void {
    this.chartOptions = null;
    this.hasData = false;
  }

  /**
   * Caches environmental data source labels for quick lookup during data processing.
   * Builds a map of data source IDs to their human-readable names.
   * This is used to label transects in the sunburst chart.
   * @returns A subscription to the data source observable.
   */
  private getDataSourceLabels(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.chartService
        .getAllEnvironmentalDataSources()
        .pipe(take(1))
        .subscribe(
          (allSources) => {
            this.environmentalDataSources = allSources || [];
            this.dataSourceLabelMap = (allSources || []).reduce((acc: Record<string, string>, src: any) => {
              if (src && src.id) {
                acc[src.id] = src.human_readable_name;
              }
              return acc;
            }, {});
            resolve();
          },
          (err) => {
            this.environmentalDataSources = [];
            this.dataSourceLabelMap = {};
            console.error('Failed to load environmental data sources', err);
            reject('Failed to load environmental data sources');
          }
        );
    });
  }

  /**
   * Extracts a list of unique vegetation values from collection metadata, preserving order.
   *
   * @param meta The collection metadata entries.
   * @returns An array of vegetation strings.
   */
  private extractVegetationValues(meta?: SpeciesCollectionMetadata[] | null): string[] {
    if (!Array.isArray(meta)) return [];

    const vegetation: string[] = [];
    const seen = new Set<string>();

    meta.forEach((entry) => {
      const value = entry?.Vegetation;
      if (typeof value !== 'string') return;
      const trimmed = value.trim();
      if (!trimmed) return;
      if (seen.has(trimmed)) return;
      seen.add(trimmed);
      vegetation.push(trimmed);
    });

    return vegetation;
  }

  /**
   * Maps a species name to the corresponding value node.
   * @param name The species name.
   * @param valueNode The value node to update.
   * @param perSpeciesValue The value to assign to the species.
   * @param sourceLabel The source label for the species.
   */
  private mapSpeciesName(name: string, valueNode: any, perSpeciesValue: number, sourceLabel?: string) {
    const speciesDisplayName = name || 'Unknown Species';

    const existing = valueNode.childrenMap[speciesDisplayName] || {
      name: speciesDisplayName,
      value: 0,
    };

    existing.value += perSpeciesValue;

    if (sourceLabel) {
      existing.sourceLabel = sourceLabel;
    }

    valueNode.childrenMap[speciesDisplayName] = existing;
  }

  /**
   * Process a single data row and update the hierarchical tree structure.
   * Uses the environmental data source ID as the unique key for transects,
   * and collection metadata to extract vegetation and species information.
   *
   * The metadata is normalized to extract common fields and vegetation values.
   * When multiple vegetation types are present, the row's value is evenly
   * distributed across each vegetation node.
   *
   * @param row The data row to process.
   * @param tree The hierarchical tree structure to update.
   */
  private processDataRow(row: SunburstRow, tree: Record<string, any>) {
    const vegetationValues = this.extractVegetationValues(row.collection_metadata);
    const transectId = row.environmental_data_source_id;
    let transectLabel = transectId;

    if (this.dataSourceLabelMap && this.dataSourceLabelMap[transectId]) {
      transectLabel = this.dataSourceLabelMap[transectId];
    }

    const vegetationList = vegetationValues;

    const entities = Array.isArray(row.entities) && row.entities.length > 0 ? row.entities : [];
    const speciesNames: string[] = [];
    if (entities.length > 0) {
      entities.forEach((e: any) => {
        if (e && e.name) speciesNames.push(e.name);
      });
    } else {
      speciesNames.push('Unknown Species');
    }

    const value = (row.sum ?? 0) as number;
    const perVegetationValue = vegetationList.length > 0 ? value / vegetationList.length : value;
    const perSpeciesValue = speciesNames.length > 0 ? perVegetationValue / speciesNames.length : perVegetationValue;
    const transectKey = transectId;
    tree[transectKey] = tree[transectKey] || { name: transectLabel, value: 0, childrenMap: {} };
    const treeNode = tree[transectKey];
    treeNode.value += value;

    const sourceLabel = transectLabel || '';

    vegetationList.forEach((vegetationName) => {
      const resolvedVegetation = vegetationName || 'Unknown Vegetation';

      treeNode.childrenMap[resolvedVegetation] = treeNode.childrenMap[resolvedVegetation] || {
        name: resolvedVegetation,
        value: 0,
        childrenMap: {},
        sourceLabel,
      };

      const valueNode = treeNode.childrenMap[resolvedVegetation];
      valueNode.value += perVegetationValue;

      speciesNames.forEach((name: string) => this.mapSpeciesName(name, valueNode, perSpeciesValue, sourceLabel));
    });
  }
  /**
   * Build a hierarchical structure from the flat data rows.
   * Builds the hierarchy with 3 levels:
   * - Centre: Transect (from metadata.transect or collection_metadata.transect)
   * - Middle: Vegetation (from collection_metadata.Vegetation or metadata.vegetation)
   * - Outer: Species (from entities[].name) and include environmentalDataSource label alongside species
   * @param rows The flat data rows to process.
   * @returns The hierarchical structure.
   */
  private buildHierarchy(rows: SunburstRow[]): any[] {
    const tree: Record<string, any> = {};

    rows.forEach((r: SunburstRow) => this.processDataRow(r, tree));

    return Object.keys(tree).map((treeKey) => {
      const treeItem = tree[treeKey];
      const vegChildren = Object.keys(treeItem.childrenMap).map((valueKey) => {
        const value = treeItem.childrenMap[valueKey];
        const speciesChildren = Object.keys(value.childrenMap).map((speciesKey) => ({
          name: value.childrenMap[speciesKey].name,
          value: value.childrenMap[speciesKey].value,
          sourceLabel: value.childrenMap[speciesKey].sourceLabel,
        }));
        return { name: value.name, value: value.value, sourceLabel: value.sourceLabel, children: speciesChildren };
      });
      const totalValue = treeItem.value ?? vegChildren.reduce((acc: number, child: any) => acc + (child.value || 0), 0);
      return { name: treeItem.name, value: totalValue, children: vegChildren };
    });
  }

  /**
   * Formats the tooltip content for the sunburst chart.
   *
   * Prefers to show "Amount" (percentage) if a sourceLabel is present,
   * otherwise shows "Count" (absolute value).
   * @param params The parameters containing the data for the tooltip.
   * @returns The formatted tooltip content as a string.
   */
  formatTooltip(params: any) {
    const dataItem = params.data || {};
    const parts: string[] = [params.marker, `<strong>${dataItem.name ?? ''}</strong>`];

    if (dataItem.sourceLabel) {
      parts.push(`<br/><em>Transect: ${dataItem.sourceLabel}</em>`);
    }

    if (dataItem.analyteName) {
      const unitSuffix = dataItem.analyteUnit ? ` ${dataItem.analyteUnit}` : '';
      parts.push(`<br/><strong>${dataItem.analyteName}${unitSuffix}</strong>`);
    }

    if (dataItem.value === undefined) {
      return parts.join('');
    }

    const formatCount = () => `<br/>Count: ${Number(dataItem.value).toLocaleString()}`;

    const percentStr = dataItem.sourceLabel ? computePercentLabelValue(params, dataItem) : null;
    parts.push(percentStr ? `<br/>Amount: ${percentStr}` : formatCount());

    return parts.join('');
  }

  /**
   * Accumulates API responses into the provided allRows array.
   * Does not build the hierarchy or update chart options — that is done
   * after all responses are processed (in a finalize block).
   * @param responses The responses from the API.
   * @param allRows The array to accumulate rows into.
   */
  populateResponses(responses: SunburstResponse[], allRows: SunburstRow[]) {
    if (!responses || responses.length === 0) return;

    responses.forEach((response) => {
      if (response && response.data) {
        response.data.forEach((row: any) => {
          const entities = Array.isArray(row?.entities)
            ? row.entities
            : Array.isArray(response?.entities)
            ? response.entities
            : row?.entities
            ? [row.entities]
            : [];

          const collectionMetadataArray = Array.isArray(row?.collection_metadata)
            ? row.collection_metadata
            : row?.collection_metadata
            ? [row.collection_metadata]
            : [];

          allRows.push({
            ...row,
            entities,
            collection_metadata: collectionMetadataArray,
          } as SunburstRow);
        });
      }
    });
  }

  /**
   * Sets the data for the sunburst chart by fetching and processing environmental data.
   * Fetches data for each data source in the configuration, processes it into a hierarchical format,
   * and updates the chart options accordingly.
   * Handles loading states and errors gracefully.
   */
  setWidgetData() {
    this.hasData = false;
    if (!this.dataSourceLabelMap || Object.keys(this.dataSourceLabelMap).length === 0) {
      return;
    }

    // If we don't have any data sources to look at, exit early.
    const preserveConfiguredLabels = Boolean(this.config?.dataConfig?.preserveConfiguredDataSourceLabels);
    const applicableDataSources = this.selectDataSources(this.config.dataConfig.dataSources, {
      preserveConfiguredLabels,
    });

    // If there's nothing to display....
    if (applicableDataSources.length === 0) {
      return;
    }
    this.chartOptions = null;

    const start = moment(this.startDate).format('YYYY-MM-DD');
    const end = moment(this.endDate).format('YYYY-MM-DD');

    const seriesData: SeriesOption[] = [];
    const allRows: SunburstRow[] = [];
    let hadError = false;

    forkJoin(
      applicableDataSources.map((source: any) =>
        this.chartService
          .getEnvironmentalDataForSourceAndAnalyteByDate(
            source.environmentalDataSourceIdentifier,
            source.analyteIdentifier,
            start,
            end,
            'day',
            source.entityNames || []
          )
          .pipe(take(1))
      )
    )
      .pipe(
        take(1),
        finalize(() => {
          if (hadError) return;

          if (allRows.length === 0) {
            this.chartOptions = {
              title: { text: 'No data is available for the selected date range.' },
            } as EChartsOption;
            return;
          } else {
            this.hasData = true;
          }

          const data = this.buildHierarchy(allRows);

          const transectSeries = {
            ...BasicSunburstChartTemplate.series,
            data,
            tooltip: { formatter: this.formatTooltip },
          };

          seriesData.push(transectSeries as SeriesOption);

          const newChartOptions: EChartsOption = this.config.echartsConfig;
          newChartOptions.series = seriesData;
          this.chartOptions = newChartOptions;
        })
      )
      .subscribe({
        next: (responses: SunburstResponse[]) => this.populateResponses(responses, allRows),
        error: (err: any) => {
          hadError = true;
          console.error(err);
          this.chartOptions = { title: { text: 'No data is available.' } } as EChartsOption;
        },
      });
  }
}
